import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Flatpickr from 'flatpickr'
import { isEqual } from 'lodash'
import { French } from 'flatpickr/dist/l10n/fr'
import clsx from 'clsx'

import { currentLang } from 'translations'
import { store } from 'store/reducers'

import './dateTimePicker.module.scss'
import { Input } from 'ui'

const hooks = ['onChange', 'onOpen', 'onClose', 'onMonthChange', 'onYearChange', 'onReady', 'onValueUpdate', 'onDayCreate']
const hookPropType = PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.func)])

export const types = {
  time: { enableTime: true, noCalendar: true, dateFormat: 'H:i', time_24hr: true, minuteIncrement: 5, step: 60 },
  timeStandard: { enableTime: true, noCalendar: true, dateFormat: 'h:i K', time_24hr: false, minuteIncrement: 5, step: 60 },
  date: { dateFormat: 'Y-m-d' },
  dateTime: { enableTime: true, time_24hr: true, minuteIncrement: 5, dateFormat: 'Y-m-d H:i' },
  dateTimeStandard: { enableTime: true, minuteIncrement: 5, dateFormat: 'Y-m-d h:i K' },
  dateMultiple: { mode: 'multiple', dateFormat: 'Y-m-d' },
  range: { mode: 'range', dateFormat: 'Y-m-d' }
}

const callbacks = ['onCreate', 'onDestroy']

const callbackPropTypes = PropTypes.func

// Config exemple:
// ------ Time picker (24h) ------
// { enableTime: true, noCalendar: true, dateFormat: 'H:i', time_24hr: true }
// ------ Date picker ------
// { dateFormat: 'Y-m-d' }
// ------ Range picker (date only) ------
// { mode: 'range', dateFormat: 'Y-m-d' }

class DateTimePicker extends Component {
  static propTypes = {
    defaultValue: PropTypes.string,
    options: PropTypes.object,
    onChange: hookPropType,
    onOpen: hookPropType,
    onClose: hookPropType,
    onMonthChange: hookPropType,
    onYearChange: hookPropType,
    onReady: hookPropType,
    onValueUpdate: hookPropType,
    onDayCreate: hookPropType,
    onCreate: callbackPropTypes,
    onDestroy: callbackPropTypes,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object, PropTypes.number]),
    children: PropTypes.node,
    className: PropTypes.string,
    render: PropTypes.func,
    fieldState: PropTypes.string,
    type: PropTypes.string
  }

  static defaultProps = {
    options: {}
  }

  initializeFlatpickr = () => {
    const presetOptions = typeof this.props.type === 'string' && typeof types[this.props.type] !== 'undefined' ? types[this.props.type] : {}

    French.firstDayOfWeek = this.props.firstDayOfTheWeek

    const options = {
      onClose: () => {
        this.node.blur && this.node.blur()
      },
      allowInput: this.props.enableDatepickerManualInput,
      defaultHour: 0,
      static: this.props.notStatic ? false : true,
      ...this.props.options,
      ...presetOptions,
      locale: this.props.lang === 'fr' ? French : { firstDayOfWeek: this.props.firstDayOfTheWeek }
    }
    // Add prop hooks to options
    hooks.forEach(hook => {
      if (this.props[hook]) {
        options[hook] = this.props[hook]
      }
    })

    this.flatpickr = new Flatpickr(this.node, options)
    if (this.props.value) {
      this.flatpickr.setDate(this.props.value, false)
    }

    const { onCreate } = this.props
    if (onCreate) onCreate(this.flatpickr)
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.lang !== this.props.lang ||
      prevProps.firstDayOfTheWeek !== this.props.firstDayOfTheWeek ||
      prevProps.enableDatepickerManualInput !== this.props.enableDatepickerManualInput
    ) {
      this.initializeFlatpickr()
    }

    if (this.props?.value !== prevProps.value) {
      if (
        !isEqual(
          this.flatpickr.selectedDates.map(date => this.flatpickr.formatDate(date, this.flatpickr.config.dateFormat)),
          this.props.value
        )
      ) {
        this.flatpickr.setDate(this.props.value, false)
      }
    }

    const { options } = this.props
    const prevOptions = prevProps.options

    hooks.forEach(hook => {
      if (this.props[hook]) {
        options[hook] = this.props[hook]
      }
      // Add prev ones too so we can compare against them later
      if (prevProps[hook]) {
        prevOptions[hook] = prevProps[hook]
      }
    })

    const optionsKeys = Object.getOwnPropertyNames(options)
    for (let index = optionsKeys.length - 1; index >= 0; index--) {
      const key = optionsKeys[index]
      let value = options[key]

      if (value !== prevOptions[key]) {
        // Hook handlers must be set as an array
        if (hooks.indexOf(key) !== -1 && !Array.isArray(value)) {
          value = [value]
        }

        this.flatpickr.set(key, value)
      }
    }
  }

  componentDidMount() {
    this.initializeFlatpickr()
  }

  componentWillUnmount() {
    const { onDestroy } = this.props
    if (onDestroy) onDestroy(this.flatpickr)
    this.flatpickr.destroy()
  }

  render() {
    const {
      options,
      className,
      defaultValue,
      descriptionKey,
      hideDescription,
      formGroupStyle,
      hideLabel,
      value,
      children,
      render,
      fieldState,
      // Remove validate from the props for the final input, otherwise html validation fails
      notStatic,
      validate,
      type,
      lang,
      enableDatepickerManualInput,
      firstDayOfTheWeek,
      ...props
    } = this.props
    const ref = node => {
      this.node = node
    }

    // Don't pass hooks and callbacks to dom node
    hooks.forEach(hook => {
      delete props[hook]
    })
    callbacks.forEach(callback => {
      delete props[callback]
    })

    if (render) return render({ ...props, defaultValue, value }, ref)

    if (options.wrap)
      return (
        <div {...props} ref={ref}>
          {children}
        </div>
      )

    if (options.inline) return <div {...props} ref={ref} />

    return <Input className={clsx(className, fieldState)} {...props} defaultValue={defaultValue} ref={ref} autoComplete="off" step={options.step || 1} />
  }
}

const WithDateTimePicker = props => {
  const {
    settings: { defaultBeginningOfWeekDay, enableDatepickerManualInput = false }
  } = store.getState()
  const lang = currentLang()
  const firstDayOfTheWeek = defaultBeginningOfWeekDay ?? 1
  const propsWithLang = { firstDayOfTheWeek, enableDatepickerManualInput, lang, ...props }

  return <DateTimePicker {...propsWithLang} />
}

export default WithDateTimePicker
